import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAccount } from '../contexts/AccountContext';
import { useToast } from '../contexts/ToastContext';
import { useApiCall } from '../hooks/useApiCall';
import { 
  RiArrowLeftLine,
  RiShoppingBagLine,
  RiCalendarLine,
  RiTimeLine,
  RiCheckboxCircleLine,
  RiCloseCircleLine,
  RiTruckLine,
  RiMapPinLine,
  RiPhoneLine,
  RiUserLine,
  RiMoneyDollarCircleLine,
  RiFileTextLine
} from 'react-icons/ri';
import '../styles/Order.css';

const Order = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const { account } = useAccount();
  const { showError } = useToast();
  const apiCall = useApiCall();
  
  const [order, setOrder] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchOrder = async () => {
      if (!account || !account.token) {
        navigate('/sign-in');
        return;
      }

      setIsLoading(true);
      try {
        const response = await apiCall('account/order/get', 'POST', {
          order_id: orderId
        }, account, null);
        
        if (response.success && response.data?.order) {
          setOrder(response.data.order);
        } else {
          showError(response.message || 'Order not found');
          navigate('/account/orders');
        }
      } catch (error) {
        console.error('Error fetching order:', error);
        showError('Failed to load order details');
        navigate('/account/orders');
      } finally {
        setIsLoading(false);
      }
    };

    fetchOrder();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
      case 'delivered':
        return <RiCheckboxCircleLine className="status-icon delivered" />;
      case 'ready_to_deliver':
      case 'shipped':
        return <RiTruckLine className="status-icon shipped" />;
      case 'pending':
      case 'processing':
        return <RiTimeLine className="status-icon processing" />;
      case 'cancelled':
        return <RiCloseCircleLine className="status-icon cancelled" />;
      default:
        return <RiTimeLine className="status-icon" />;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'completed':
        return 'Completed';
      case 'delivered':
        return 'Delivered';
      case 'ready_to_deliver':
        return 'Ready to Deliver';
      case 'shipped':
        return 'Shipped';
      case 'pending':
        return 'Pending';
      case 'processing':
        return 'Processing';
      case 'cancelled':
        return 'Cancelled';
      default:
        return status || 'Unknown';
    }
  };

  const formatDate = (timestamp) => {
    return new Date(timestamp * 1000).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const parseContactInfo = (contactJson) => {
    try {
      if (!contactJson) return null;
      return typeof contactJson === 'string' ? JSON.parse(contactJson) : contactJson;
    } catch (error) {
      console.error('Error parsing contact info:', error);
      return null;
    }
  };

  if (isLoading) {
    return (
      <div className="order-container">
        <div className="order-content">
          <div className="loading-state">Loading order details...</div>
        </div>
      </div>
    );
  }

  if (!order) {
    return null;
  }

  const contactInfo = parseContactInfo(order.contact);

  return (
    <div className="order-container">
      <div className="order-content">
        <div className="order-header-section">
          <button className="back-btn" onClick={() => navigate('/account/orders')}>
            <RiArrowLeftLine />
            Back to Orders
          </button>

          <div className="order-title-section">
            <div className="order-title">
              <RiShoppingBagLine className="order-icon" />
              <h1>Order #{order.id}</h1>
            </div>
            <div className="order-status-badge">
              {getStatusIcon(order.status)}
              <span className={`status-text ${order.status}`}>
                {getStatusText(order.status)}
              </span>
            </div>
          </div>

          <div className="order-meta">
            <div className="meta-item">
              <RiCalendarLine />
              <span>Placed on {formatDate(order.timestamp_placed)}</span>
            </div>
            {order.timestamp_last !== order.timestamp_placed && (
              <div className="meta-item">
                <RiTimeLine />
                <span>Last updated {formatDate(order.timestamp_last)}</span>
              </div>
            )}
          </div>
        </div>

        <div className="order-details-grid">
          {/* Order Items */}
          <div className="order-section order-items-section">
            <h2 className="section-title">Order Items</h2>
            <div className="items-list">
              {order.items && order.items.map((item, index) => {
                // Create slug for product URL
                const createSlug = (name) => {
                  return name
                    .toLowerCase()
                    .replace(/[^a-z0-9\s&]/g, '')
                    .replace(/\s*&\s*/g, '-and-')
                    .replace(/\s+/g, '-')
                    .replace(/-+/g, '-')
                    .replace(/^-|-$/g, '');
                };
                const productSlug = createSlug(item.name);
                
                return (
                <div key={index} className="item-card">
                  <div 
                    className="item-image"
                    onClick={() => navigate(`/products/product/${item.product_id}/${productSlug}`)}
                    style={{ cursor: 'pointer' }}
                  >
                    {item.primary_image ? (
                      <img 
                        src={item.primary_image} 
                        alt={item.name}
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                    ) : null}
                    <div className="item-image-placeholder" style={{ display: item.primary_image ? 'none' : 'flex' }}>
                      <RiShoppingBagLine />
                    </div>
                  </div>
                  <div className="item-details">
                    <h3 
                      onClick={() => navigate(`/products/product/${item.product_id}/${productSlug}`)}
                      style={{ cursor: 'pointer' }}
                    >
                      {item.name}
                    </h3>
                    {item.options && (() => {
                      try {
                        const options = typeof item.options === 'string' ? JSON.parse(item.options) : item.options;
                        if (options && options.modifiers && options.modifiers.length > 0) {
                          return (
                            <div className="item-modifiers">
                              {options.modifiers.map((modifier, idx) => (
                                <div key={idx} className="modifier">
                                  <strong>{modifier.name}:</strong> {modifier.option?.item || 'N/A'}
                                  {modifier.option?.amount > 0 && <span className="modifier-price"> (+${parseFloat(modifier.option.amount).toFixed(2)})</span>}
                                </div>
                              ))}
                            </div>
                          );
                        }
                      } catch (error) {
                        console.error('Error parsing options:', error);
                      }
                      return null;
                    })()}
                    <div className="item-quantity">Quantity: {item.qty}</div>
                  </div>
                  <div className="item-price">
                    <div className="price-label">Total</div>
                    <div className="price-value">{item.amount_curr} {(parseFloat(item.amount) * parseInt(item.qty)).toFixed(2)}</div>
                  </div>
                </div>
                );
              })}
            </div>

            <div className="order-total-section">
              <div className="total-row">
                <span className="total-label">Order Total</span>
                <span className="total-value">{order.totals_curr} {parseFloat(order.totals).toFixed(2)}</span>
              </div>
            </div>
          </div>

          {/* Order Information */}
          <div className="order-sidebar">
            {/* Order Type */}
            <div className="order-section">
              <h2 className="section-title">Order Type</h2>
              <div className="info-card">
                <div className="info-item">
                  <RiTruckLine className="info-icon" />
                  <span className="order-type-badge">
                    {order.type === 'delivery' ? 'Delivery' : 'Pickup'}
                  </span>
                </div>
              </div>
            </div>

            {/* Contact Information */}
            {contactInfo && contactInfo.contact && (
              <div className="order-section">
                <h2 className="section-title">Contact Information</h2>
                <div className="info-card">
                  {contactInfo.contact.name && (
                    <div className="info-item">
                      <RiUserLine className="info-icon" />
                      <div>
                        <div className="info-label">Name</div>
                        <div className="info-value">{contactInfo.contact.name}</div>
                      </div>
                    </div>
                  )}
                  {contactInfo.contact.phone && (
                    <div className="info-item">
                      <RiPhoneLine className="info-icon" />
                      <div>
                        <div className="info-label">Phone</div>
                        <div className="info-value">{contactInfo.contact.phone}</div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Delivery Address */}
            {order.type === 'delivery' && contactInfo && contactInfo.address && (
              <div className="order-section">
                <h2 className="section-title">Delivery Address</h2>
                <div className="info-card">
                  <div className="info-item">
                    <RiMapPinLine className="info-icon" />
                    <div>
                      <div className="info-value">
                        {contactInfo.address.address} {contactInfo.address.address_num}
                      </div>
                      {/* {contactInfo.address.location && (
                        <div className="info-label">
                          Lat: {contactInfo.address.location.latitude}, 
                          Lng: {contactInfo.address.location.longitude}
                        </div>
                      )} */}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Payment Information */}
            <div className="order-section">
              <h2 className="section-title">Payment</h2>
              <div className="info-card">
                <div className="info-item">
                  <RiMoneyDollarCircleLine className="info-icon" />
                  <div>
                    <div className="info-label">Payment Method</div>
                    <div className="info-value payment-method">
                      {order.payment === 'cash' ? 'Cash' : order.payment === 'card' ? 'Card' : order.payment}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Comments */}
            {order.comments && order.comments.trim() !== '' && (
              <div className="order-section">
                <h2 className="section-title">Order Comments</h2>
                <div className="info-card">
                  <div className="info-item">
                    <RiFileTextLine className="info-icon" />
                    <div className="info-value">{order.comments}</div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Order;
